import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
    fetchPropertyUsers,
    addPropertyUser,
    addPropertyContact,
    updatePropertyUser,
    removePropertyUser,
    fetchAssignableUsers,
    setPage,
    setPageSize,
} from "./property-users.slice";
import { useEffect, useState } from "react";
import { PropertyUserMetadata } from "@/models/properties.model";
import { PropertyUserDisplayModel } from "./property-users.entity";
import { uuid } from "@/interfaces";
import { setPropertyManager } from "@/app/pmc/properties-detail/properties-detail.slice";
import {User} from "@/models/user.model.ts";
import { notYetAssigned } from "@/repositories/property-people-records";

async function succeeded(action: Promise<unknown>): Promise<boolean> {
    try {
        await action;
        return true;
    } catch {
        return false;
    }
}

export const usePropertyUsers = (propertyId: uuid) => {
    const dispatch = useAppDispatch();

    const {
        users,
        assignableUsers,
        loading,
        filters,
        total,
        saving,
        deleting,
    } = useAppSelector((state) => state.propertyUsers);

    const pagedUsers = users.slice(
        (filters.page - 1) * filters.pageSize,
        filters.page * filters.pageSize
    );

    const [assigning, setAssigning] = useState(false);
    const [addingContact, setAddingContact] = useState(false);
    const [editing, setEditing] = useState<PropertyUserDisplayModel | null>(null);

    useEffect(() => {
        if (propertyId) {
            dispatch(fetchPropertyUsers(propertyId));
            dispatch(fetchAssignableUsers());
        }
    }, [propertyId, dispatch]);

    const addUser = async (
        userId: uuid,
        meta: PropertyUserMetadata,
        isPrimaryContact: boolean
    ): Promise<boolean> => {
        if (!(await succeeded(dispatch(addPropertyUser({ propertyId, userId, metaData: meta, isPrimaryContact })).unwrap()))) return false;
        dispatch(fetchPropertyUsers(propertyId));

        if (isPrimaryContact) {
            const user = assignableUsers.find((u) => u.id === userId);
            if (user) dispatch(setPropertyManager(user as User));
        }
        return true;
    };

    const addContact = async (email: string, meta: PropertyUserMetadata, isPrimaryContact: boolean): Promise<boolean> => {
        if (!(await succeeded(dispatch(addPropertyContact({ propertyId, email, metaData: meta, isPrimaryContact })).unwrap()))) return false;
        dispatch(fetchPropertyUsers(propertyId));
        return true;
    };

    const updateUser = async (
        id: uuid,
        meta: PropertyUserMetadata,
        isPrimaryContact: boolean
    ): Promise<boolean> => {
        if (!(await succeeded(dispatch(updatePropertyUser({ propertyId, id, metaData: meta, isPrimaryContact })).unwrap()))) return false;
        dispatch(fetchPropertyUsers(propertyId));
        setEditing(null);

        if (isPrimaryContact) {
            const match = users.find((u) => u.id === id);
            if (match?.user) dispatch(setPropertyManager(match.user));
        }
        return true;
    };

    const removeUser = async (id: uuid): Promise<boolean> => {
        if (!(await succeeded(dispatch(removePropertyUser({ propertyId, id })).unwrap()))) return false;
        dispatch(fetchPropertyUsers(propertyId));
        return true;
    };

    return {
        users: pagedUsers,
        assignableUsers: notYetAssigned(assignableUsers, users),
        total,
        page: filters.page,
        pageSize: filters.pageSize,
        loading,
        saving,
        deleting,
        assigning,
        setPage: (p: number) => dispatch(setPage(p)),
        setPageSize: (s: number) => dispatch(setPageSize(s)),
        addUser,
        addContact,
        addingContact,
        openAddContactModal: () => setAddingContact(true),
        closeAddContactModal: () => setAddingContact(false),
        updateUser,
        removeUser,
        editing,
        openEditModal: (u: PropertyUserDisplayModel) => setEditing(u),
        closeEditModal: () => setEditing(null),
        assigningModalOpen: assigning,
        openAssignModal: () => setAssigning(true),
        closeAssignModal: () => setAssigning(false),
    };
};
